export const getDeviceType = (): "mobile" | "tablet" | "desktop" => {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
};

export const isMobile = (): boolean => getDeviceType() === "mobile";
export const isTablet = (): boolean => getDeviceType() === "tablet";
export const isDesktop = (): boolean => getDeviceType() === "desktop";

export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

export const scrollToTop = (behavior: ScrollBehavior = "smooth") => {
  window.scrollTo({ top: 0, behavior });
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const getSearchSnippet = (
  content: string,
  query: string,
  contextLength: number = 100
): string => {
  if (!query) return "";

  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerContent.indexOf(lowerQuery);

  if (index === -1) return "";

  let start = Math.max(0, index - contextLength);
  let end = Math.min(content.length, index + query.length + contextLength);

  // Try to start at word boundary
  if (start > 0) {
    const spaceIndex = content.indexOf(" ", start);
    if (spaceIndex !== -1 && spaceIndex < index) {
      start = spaceIndex + 1;
    }
  }

  // Try to end at word boundary
  if (end < content.length) {
    const spaceIndex = content.lastIndexOf(" ", end);
    if (spaceIndex !== -1 && spaceIndex > index + query.length) {
      end = spaceIndex;
    }
  }

  let snippet = content.substring(start, end).trim();

  // Clean up markdown
  snippet = snippet
    .replace(/#+\s/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^[-*+]\s/gm, "")
    .replace(/^\d+\.\s/gm, "")
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ");

  if (start > 0) snippet = "..." + snippet;
  if (end < content.length) snippet = snippet + "...";

  return snippet;
};

export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/["'`]/g, "") // Remove quotes
    .replace(/[^a-z0-9-]/g, "-") // Replace invalid chars with -
    .replace(/-+/g, "-") // Collapse dashes
    .replace(/^-+|-+$/g, ""); // Trim leading/trailing dashes
};
